"use client";

import * as React from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Loader2,
  XCircle,
} from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { createVenue, checkSlug } from "@/services/venue";
import { useOrganisations } from "@/hooks/use-organisations";
import type { CreateVenueDto } from "@/types";
import { cn, isPhoneValid } from "@/lib/utils";

interface AddVenueDialogProps {
  children?: React.ReactNode;
  onSuccess?: () => void;
}

export function AddVenueDialog({ children, onSuccess }: AddVenueDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedOrganisationId, setSelectedOrganisationId] = React.useState<
    string | null
  >(null);
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugEdited, setSlugEdited] = React.useState(false);
  const [slugStatus, setSlugStatus] = React.useState<
    "idle" | "checking" | "available" | "taken" | "invalid"
  >("idle");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("us");
  const [email, setEmail] = React.useState("");
  const [address, setAddress] = React.useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
  });

  // Refs for focusing the first invalid mandatory field on submit
  const nameRef = React.useRef<HTMLInputElement>(null);
  const slugRef = React.useRef<HTMLInputElement>(null);
  const organisationButtonRef = React.useRef<HTMLButtonElement>(null);
  const addressLine1Ref = React.useRef<HTMLInputElement>(null);
  const cityRef = React.useRef<HTMLInputElement>(null);
  const stateRef = React.useRef<HTMLInputElement>(null);
  const zipcodeRef = React.useRef<HTMLInputElement>(null);
  const countryRef = React.useRef<HTMLInputElement>(null);

  const focusField = (
    ref: React.RefObject<HTMLInputElement | HTMLButtonElement | null>
  ) => {
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  const queryClient = useQueryClient();

  const resetForm = React.useCallback(() => {
    setName("");
    setSlug("");
    setSlugEdited(false);
    setSlugStatus("idle");
    setPhoneNumber("");
    setEmail("");
    setSelectedOrganisationId(null);
    setSearchQuery("");
    setAddress({
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      country: "",
    });
  }, []);

  // Fetch all organisations
  const { data: organisationsData, isLoading: isLoadingOrganisations } =
    useOrganisations({
      pageNumber: 1,
      pageSize: 1000,
      search: searchQuery || undefined,
    });

  // Auto-generate slug from venue name until the user manually edits it.
  // When the name is cleared, fully reset the slug state so the field doesn't
  // get stuck disabled with a stale value.
  React.useEffect(() => {
    if (!name.trim()) {
      setSlug("");
      setSlugEdited(false);
      setSlugStatus("idle");
      return;
    }
    if (slugEdited) return;
    const generated = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    // Mark as checking immediately so the loader is visible during the brief
    // generating phase before the debounced availability check fires.
    if (generated) setSlugStatus("checking");
    setSlug(generated);
  }, [name, slugEdited]);

  // Debounced slug availability check
  React.useEffect(() => {
    const trimmed = slug.trim();

    if (!trimmed) {
      setSlugStatus("idle");
      return;
    }

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmed)) {
      setSlugStatus("invalid");
      return;
    }

    setSlugStatus("checking");
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { exists } = await checkSlug(trimmed);
        if (cancelled) return;
        setSlugStatus(exists ? "taken" : "available");
      } catch (err) {
        if (cancelled) return;
        console.error("Error checking slug:", err);
        setSlugStatus("idle");
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slug]);

  // Mutation for creating a venue
  const mutation = useMutation({
    mutationFn: (data: CreateVenueDto) => createVenue(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["venues"] });
      toast.success(`Venue "${data.name}" created successfully!`);

      resetForm();
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "Failed to create venue";
      toast.error(errorMessage);
      console.error("Error creating venue:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Venue name is required");
      focusField(nameRef);
      return;
    }

    const trimmedSlug = slug.trim();
    if (!trimmedSlug) {
      toast.error("Slug is required");
      focusField(slugRef);
      return;
    }
    if (slugStatus === "invalid") {
      toast.error("Slug must be lowercase letters, numbers, and hyphens only");
      focusField(slugRef);
      return;
    }
    if (slugStatus === "taken") {
      toast.error(
        "This slug is not available. Please edit it to a unique one."
      );
      focusField(slugRef);
      return;
    }
    if (slugStatus === "checking") {
      toast.error("Please wait while we check slug availability");
      focusField(slugRef);
      return;
    }

    if (!selectedOrganisationId) {
      toast.error("Organisation is required");
      focusField(organisationButtonRef);
      return;
    }

    // Validate address fields - all are required
    if (!address.addressLine1.trim()) {
      toast.error("Address Line 1 is required");
      focusField(addressLine1Ref);
      return;
    }
    if (!address.city.trim()) {
      toast.error("City is required");
      focusField(cityRef);
      return;
    }
    if (!address.state.trim()) {
      toast.error("State is required");
      focusField(stateRef);
      return;
    }
    if (!address.zipcode.trim()) {
      toast.error("Zipcode is required");
      focusField(zipcodeRef);
      return;
    }
    if (!address.country.trim()) {
      toast.error("Country is required");
      focusField(countryRef);
      return;
    }

    if (phoneNumber.trim()) {
      const isValid = isPhoneValid(phoneNumber.trim(), countryCode);
      if (!isValid) {
        toast.error("Please enter a valid phone number");
        return;
      }
    }

    const payload: CreateVenueDto = {
      name: name.trim(),
      slug: trimmedSlug,
      phoneNumber: phoneNumber.trim() || undefined,
      email: email.trim() || undefined,
      organisationId: selectedOrganisationId,
      address: {
        addressLine1: address.addressLine1.trim(),
        addressLine2: address.addressLine2.trim(),
        city: address.city.trim(),
        state: address.state.trim(),
        zipcode: address.zipcode.trim(),
        country: address.country.trim(),
      },
    };

    mutation.mutate(payload);
  };

  const selectedOrganisation = organisationsData?.data?.find(
    (org) => org.id === selectedOrganisationId
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetForm();
      }}
    >
      <DialogTrigger asChild>
        {children || <Button>Add Venue</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] !flex !flex-col max-h-[85vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Add New Venue</DialogTitle>
          <DialogDescription>
            Create a new venue. Fill in the details below.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="grid gap-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
            {/* Venue Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">
                Venue Name <span className="text-destructive">*</span>
              </Label>
              <Input
                ref={nameRef}
                id="name"
                placeholder="e.g., Grand Ballroom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Slug */}
            <div className="grid gap-2">
              <Label htmlFor="slug">
                Slug <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  ref={slugRef}
                  id="slug"
                  placeholder="Type a venue name to generate a slug"
                  value={slug}
                  disabled={!name.trim()}
                  required
                  aria-invalid={
                    slugStatus === "taken" || slugStatus === "invalid"
                  }
                  aria-describedby="slug-status"
                  onChange={(e) => {
                    const next = e.target.value
                      .toLowerCase()
                      .replace(/\s+/g, "-");
                    // If the user clears the slug, fall back to auto-generation
                    // so editing the name again repopulates the slug.
                    setSlugEdited(next.length > 0);
                    setSlug(next);
                  }}
                  className={cn(
                    "pr-9",
                    slugStatus === "taken" || slugStatus === "invalid"
                      ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30"
                      : slugStatus === "available"
                      ? "border-green-500 focus-visible:border-green-500 focus-visible:ring-green-500/30"
                      : ""
                  )}
                />
                {slugStatus === "checking" && (
                  <Loader2
                    aria-hidden="true"
                    className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
                  />
                )}
                {slugStatus === "available" && (
                  <CheckCircle2
                    aria-hidden="true"
                    className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-green-600"
                  />
                )}
                {(slugStatus === "taken" || slugStatus === "invalid") && (
                  <XCircle
                    aria-hidden="true"
                    className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-destructive"
                  />
                )}
              </div>
              <p
                id="slug-status"
                role={
                  slugStatus === "taken" || slugStatus === "invalid"
                    ? "alert"
                    : "status"
                }
                aria-live="polite"
                className={cn(
                  "text-xs",
                  slugStatus === "taken" || slugStatus === "invalid"
                    ? "text-destructive"
                    : slugStatus === "available"
                    ? "text-green-600"
                    : "sr-only"
                )}
              >
                {slugStatus === "invalid"
                  ? "Use lowercase letters, numbers, and hyphens only."
                  : slugStatus === "taken"
                  ? "This slug is not available. Please edit the slug to a unique one."
                  : slugStatus === "available"
                  ? "Slug is available."
                  : ""}
              </p>
            </div>

            {/* Phone Number */}
            <div className="grid gap-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <PhoneInput
                defaultCountry="us"
                preferredCountries={["us"]}
                forceDialCode={true}
                value={phoneNumber}
                onChange={(phone, meta) => {
                  if (phone.length >= 5) {
                    setCountryCode(meta.country.iso2);
                    setPhoneNumber(phone);
                  }
                }}
                className="[&_.react-international-phone-input-container]:rounded-md! [&_.react-international-phone-input]:bg-transparent! [&_.react-international-phone-input]:dark:bg-input/30! [&_.react-international-phone-input]:border-input! [&_.react-international-phone-input]:text-foreground! [&_.react-international-phone-input]:rounded-r-md! [&_.react-international-phone-input]:rounded-l-none!"
                inputClassName="h-9 w-full min-w-0 rounded-r-md rounded-l-none border border-input bg-transparent dark:bg-input/30 px-3 py-1 text-base md:text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 !bg-transparent dark:!bg-input/30 !border-input !rounded-r-md !rounded-l-none !text-foreground"
                countrySelectorStyleProps={{
                  buttonClassName:
                    "flex h-9 items-center justify-center !rounded-l-md border border-input !bg-transparent dark:!bg-input/30 px-3 hover:bg-accent hover:text-accent-foreground !border-input !text-foreground",
                  dropdownStyleProps: {
                    className:
                      "!bg-popover !text-popover-foreground rounded-md border !border-input shadow-md z-50 !p-1",
                    listItemClassName:
                      "!px-3 !py-2 hover:!bg-accent hover:!text-accent-foreground focus:!bg-accent focus:!text-accent-foreground focus-within:!bg-accent focus-within:!text-accent-foreground cursor-pointer !rounded-sm transition-colors !text-sm !text-foreground",
                    listItemSelectedClassName:
                      "!bg-accent !text-accent-foreground",
                  },
                }}
              />
            </div>

            {/* Email */}
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="e.g., contact@venue.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Organisation */}
            <div className="grid gap-2">
              <Label>
                Organisation <span className="text-destructive">*</span>
              </Label>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    ref={organisationButtonRef}
                    variant="outline"
                    role="combobox"
                    aria-expanded={popoverOpen}
                    className="justify-between"
                  >
                    {selectedOrganisation
                      ? selectedOrganisation.name
                      : "Select organisation..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search organisation..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {isLoadingOrganisations
                          ? "Loading..."
                          : "No organisation found."}
                      </CommandEmpty>
                      <CommandGroup>
                        {organisationsData?.data?.map((organisation) => (
                          <CommandItem
                            key={organisation.id}
                            value={organisation.name}
                            onSelect={() => {
                              setSelectedOrganisationId(
                                selectedOrganisationId === organisation.id
                                  ? null
                                  : organisation.id
                              );
                              setPopoverOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedOrganisationId === organisation.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {organisation.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Address Section */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-3">
                Address <span className="text-destructive">*</span>
              </h3>

              {/* Address Line 1 */}
              <div className="grid gap-2 mb-3">
                <Label htmlFor="addressLine1">
                  Address Line 1 <span className="text-destructive">*</span>
                </Label>
                <Input
                  ref={addressLine1Ref}
                  id="addressLine1"
                  placeholder="Street address"
                  value={address.addressLine1}
                  onChange={(e) =>
                    setAddress({ ...address, addressLine1: e.target.value })
                  }
                  required
                />
              </div>

              {/* Address Line 2 */}
              <div className="grid gap-2 mb-3">
                <Label htmlFor="addressLine2">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  placeholder="Apartment, suite, etc."
                  value={address.addressLine2}
                  onChange={(e) =>
                    setAddress({ ...address, addressLine2: e.target.value })
                  }
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="grid gap-2">
                  <Label htmlFor="city">
                    City <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    ref={cityRef}
                    id="city"
                    placeholder="City"
                    value={address.city}
                    onChange={(e) =>
                      setAddress({ ...address, city: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="state">
                    State <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    ref={stateRef}
                    id="state"
                    placeholder="State"
                    value={address.state}
                    onChange={(e) =>
                      setAddress({ ...address, state: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              {/* Zipcode and Country */}
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="zipcode">
                    Zipcode <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    ref={zipcodeRef}
                    id="zipcode"
                    placeholder="10001"
                    value={address.zipcode}
                    onChange={(e) =>
                      setAddress({ ...address, zipcode: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="country">
                    Country <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    ref={countryRef}
                    id="country"
                    placeholder="United States"
                    value={address.country}
                    onChange={(e) =>
                      setAddress({ ...address, country: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm();
                setOpen(false);
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                !slug.trim() ||
                slugStatus === "checking" ||
                slugStatus === "taken" ||
                slugStatus === "invalid"
              }
            >
              {mutation.isPending ? "Creating..." : "Create Venue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
